import { Component, OnInit } from '@angular/core';
import { LeaderboardService } from '../services/leaderboard.service';
import { LeaderboardEntry, PROCESSOR_LEVELS, ProcessorLevel } from '../models/leaderboard.models';

interface RankedProcessor extends LeaderboardEntry {
  rank: number;
  compositeScore: number;
  qualityRate: number;
  level: ProcessorLevel;
  stars: number;
  progressToNext: number;
}

@Component({
  selector: 'app-leaderboard',
  templateUrl: './leaderboard.component.html',
  styleUrls: ['./leaderboard.component.less'],
})
export class LeaderboardComponent implements OnInit {
  processors: RankedProcessor[] = [];
  loading = true;
  error: string | null = null;
  daysBack = 30;

  // Plotly chart configs
  volumeChart: { data: any[]; layout: any } = { data: [], layout: {} };
  speedChart: { data: any[]; layout: any } = { data: [], layout: {} };
  qualityChart: { data: any[]; layout: any } = { data: [], layout: {} };
  scoreChart: { data: any[]; layout: any } = { data: [], layout: {} };

  plotConfig = { displayModeBar: false, responsive: true };
  levels = PROCESSOR_LEVELS;

  // Floating particles
  particlePositions = Array.from({ length: 20 }, () => ({
    x: Math.random() * 100,
    delay: Math.random() * 8,
  }));

  constructor(private leaderboardSvc: LeaderboardService) {}

  ngOnInit(): void {
    this.loadData();
  }

  loadData(): void {
    this.loading = true;
    this.error = null;

    this.leaderboardSvc.getLeaderboard(this.daysBack).subscribe({
      next: (res) => {
        this.processors = this.rankProcessors(res.entries);
        this.buildCharts();
        this.loading = false;
      },
      error: () => {
        this.error = 'Failed to load leaderboard data.';
        this.loading = false;
      },
    });
  }

  onDaysChange(): void {
    this.loadData();
  }

  private rankProcessors(entries: LeaderboardEntry[]): RankedProcessor[] {
    const scored = entries.map((e) => {
      const qualityRate = this.calcQualityRate(e);
      const compositeScore = this.calcCompositeScore(e, qualityRate);
      const level = this.getLevel(compositeScore);
      const nextLevel = PROCESSOR_LEVELS.find((l) => l.minScore > compositeScore);
      const progressToNext = nextLevel
        ? ((compositeScore - level.minScore) / (nextLevel.minScore - level.minScore)) * 100
        : 100;
      const stars = this.calcStars(compositeScore, level, nextLevel);

      return {
        ...e,
        rank: 0,
        compositeScore: Math.round(compositeScore),
        qualityRate,
        level,
        stars,
        progressToNext: Math.min(100, Math.round(progressToNext)),
      };
    });

    scored.sort((a, b) => b.compositeScore - a.compositeScore);
    scored.forEach((p, i) => (p.rank = i + 1));

    return scored;
  }

  private calcQualityRate(e: LeaderboardEntry): number {
    if (e.auditedCount === 0) return 100;
    const passRate = (e.auditPassCount / e.auditedCount) * 100;
    const issuePenalty = e.qaIssueCount > 0 ? (e.qaIssueCount / e.completedCount) * 50 : 0;
    return Math.max(0, Math.min(100, passRate - issuePenalty));
  }

  private calcCompositeScore(e: LeaderboardEntry, qualityRate: number): number {
    // Volume: completions are the base (1 pt each)
    const volumeScore = e.completedCount * 2;

    // Speed bonus: faster than 10 min avg earns bonus points
    const speedBonus = e.avgDurationSeconds > 0 && e.avgDurationSeconds < 600
      ? Math.round((600 - e.avgDurationSeconds) / 60) * 2
      : 0;

    // Quality multiplier: high quality amplifies score, low quality dampens it
    const qualityMultiplier = qualityRate / 100;

    // Streak bonus: consecutive work days
    const streakBonus = e.currentStreakDays * 3;

    // QA issue penalty: direct deduction
    const issuePenalty = e.qaIssueCount * 5 + e.qaIssueHighSevCount * 10;

    // Consistency bonus: active days matter
    const consistencyBonus = e.activeDaysCount * 2;

    const raw = volumeScore + speedBonus + streakBonus + consistencyBonus - issuePenalty;
    return Math.max(0, raw * qualityMultiplier);
  }

  private getLevel(score: number): ProcessorLevel {
    let result = PROCESSOR_LEVELS[0];
    for (const level of PROCESSOR_LEVELS) {
      if (score >= level.minScore) result = level;
    }
    return result;
  }

  private calcStars(score: number, current: ProcessorLevel, next: ProcessorLevel | undefined): number {
    if (!next) return 3;
    const range = next.minScore - current.minScore;
    const progress = score - current.minScore;
    const pct = progress / range;
    if (pct >= 0.66) return 3;
    if (pct >= 0.33) return 2;
    return 1;
  }

  private buildCharts(): void {
    const names = this.processors.map((p) => p.processorName);
    const colors = this.processors.map((p) => p.level.color);
    const darkLayout = {
      margin: { t: 40, b: 80, l: 40, r: 20 },
      paper_bgcolor: 'transparent',
      plot_bgcolor: 'transparent',
      font: { color: '#a7a9be', size: 11 },
      xaxis: { tickangle: -45, tickfont: { size: 10, color: '#a7a9be' }, gridcolor: 'rgba(255,255,255,0.05)' },
      yaxis: { gridcolor: 'rgba(255,255,255,0.05)', tickfont: { color: '#a7a9be' } },
      height: 280,
    };

    this.volumeChart = {
      data: [{
        x: names,
        y: this.processors.map((p) => p.completedCount),
        type: 'bar',
        marker: { color: colors, line: { width: 0 } },
        hovertemplate: '%{x}<br>%{y} verifications<extra></extra>',
      }],
      layout: { ...darkLayout, title: { text: '🎯 Verifications', font: { size: 14, color: '#fffffe' } } },
    };

    this.speedChart = {
      data: [{
        x: names,
        y: this.processors.map((p) => Math.round(p.avgDurationSeconds / 60)),
        type: 'bar',
        marker: {
          color: this.processors.map((p) =>
            p.avgDurationSeconds < 300 ? '#2cb67d' : p.avgDurationSeconds < 600 ? '#ff8906' : '#e53170'
          ),
          line: { width: 0 },
        },
        hovertemplate: '%{x}<br>%{y} min avg<extra></extra>',
      }],
      layout: { ...darkLayout, title: { text: '⚡ Avg Time (min)', font: { size: 14, color: '#fffffe' } } },
    };

    this.qualityChart = {
      data: [
        { x: names, y: this.processors.map((p) => p.auditPassCount), name: 'Pass', type: 'bar', marker: { color: '#2cb67d' } },
        { x: names, y: this.processors.map((p) => p.auditConcernsCount), name: 'Concerns', type: 'bar', marker: { color: '#ff8906' } },
        { x: names, y: this.processors.map((p) => p.auditFailCount), name: 'Fail', type: 'bar', marker: { color: '#e53170' } },
        { x: names, y: this.processors.map((p) => -p.qaIssueCount), name: 'QA Issues', type: 'bar', marker: { color: '#7f5af0' } },
      ],
      layout: {
        ...darkLayout,
        title: { text: '🛡️ Quality', font: { size: 14, color: '#fffffe' } },
        barmode: 'relative',
        legend: { orientation: 'h', y: -0.35, font: { color: '#a7a9be' } },
      },
    };

    this.scoreChart = {
      data: [{
        x: names,
        y: this.processors.map((p) => p.compositeScore),
        type: 'bar',
        marker: { color: colors, line: { width: 0 } },
        hovertemplate: '%{x}<br>Score: %{y}<extra></extra>',
      }],
      layout: { ...darkLayout, title: { text: '👑 Composite Score', font: { size: 14, color: '#fffffe' } } },
    };
  }

  formatDuration(seconds: number): string {
    if (!seconds || seconds <= 0) return '—';
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
  }

  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(amount);
  }

  getStarsArray(count: number): number[] {
    return Array.from({ length: 3 }, (_, i) => (i < count ? 1 : 0));
  }

  getRankBadge(rank: number): string {
    if (rank === 1) return '🥇';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return `#${rank}`;
  }
}
